import type { ReactNode } from "react";
import { useContext } from "react";
import moment from "moment";
import InfoToolTip from "@/components/muiCustom/tooltip/InfoToolTip";
import { GlobalContext } from "@/helpers/contexts/contexts";

type RRDToolTipType =
  | "hero_images"
  | "coupons"
  | "estimated_distribution"
  | "distribution_cost"
  | "store_copy"
  | "mailing_date"
  | "coupon_expiration_date"
  | "market";

type RRDDirectMailToolTipsProps = {
  type?: RRDToolTipType | string;
  customContent?: ReactNode;
  minCouponDurationDays?: number | string | null;
  minCouponExpirationDate?: moment.MomentInput | null;
  maxCouponExpirationDate?: moment.MomentInput | null;
};

type SelectedBrandWithName = {
  brand_name?: string;
};

type RRDToolTipContext = {
  brandName: string;
  minCouponDurationDays?: number | string | null;
  minCouponExpirationDate?: moment.MomentInput | null;
  maxCouponExpirationDate?: moment.MomentInput | null;
};

type RRDToolTipContent = string | ((_context: RRDToolTipContext) => string);

function getFormattedDate(value?: moment.MomentInput | null) {
  if (!value) return null;

  const date = moment(value);
  return date.isValid() ? date.format("MMM DD, YYYY") : null;
}

function getCouponExpirationTooltip({
  minCouponDurationDays,
  minCouponExpirationDate,
  maxCouponExpirationDate,
}: RRDToolTipContext) {
  const durationDays = Number(minCouponDurationDays);
  const hasDurationDays = Number.isFinite(durationDays) && durationDays > 0;
  const formattedMinDate = getFormattedDate(minCouponExpirationDate);
  const formattedMaxDate = getFormattedDate(maxCouponExpirationDate);

  if (formattedMinDate && formattedMinDate === formattedMaxDate) {
    return `Coupons on the printed piece will expire on ${formattedMinDate}.`;
  }

  if (formattedMinDate && formattedMaxDate) {
    return `Coupons on the printed piece will expire between ${formattedMinDate} and ${formattedMaxDate}.`;
  }

  if (formattedMinDate) {
    return `Coupons on the printed piece will expire on or after ${formattedMinDate}.`;
  }

  if (hasDurationDays) {
    return `Coupons on the printed piece will expire at least ${durationDays} ${
      durationDays === 1 ? "day" : "days"
    } from the mailing week.`;
  }

  if (formattedMaxDate) {
    return `Coupons on the printed piece will expire no later than ${formattedMaxDate}.`;
  }

  return "Coupons on the printed piece will expire based on the selected mailing week.";
}

const toolTipContent: Record<RRDToolTipType, RRDToolTipContent> = {
  hero_images:
    "The main visual used to promote the mailer and the accompanying coupon offers.",
  coupons: "Select which offers you want on the mailer.",
  estimated_distribution:
    "Expected number of mailers to be delivered to consumer households allocated to your specific store location.",
  distribution_cost:
    "Cost to distribute the mailers (includes print, shipping, and taxes).",
  store_copy: ({ brandName }) =>
    `Store copy is delivered to your ${brandName} location in increments of 1,000. Select yes or no if you would like to opt-in for store copy, to be handed out to customers in-store.`,
  mailing_date:
    "Based on mailing availability, please select which week you’d like the printed piece to be received by consumers in their mailbox.",
  coupon_expiration_date: getCouponExpirationTooltip,
  market: "The DMA your store falls within, based on data from Corporate.",
};

function RRDDirectMailToolTips({
  type = "",
  customContent = null,
  minCouponDurationDays,
  minCouponExpirationDate,
  maxCouponExpirationDate,
}: RRDDirectMailToolTipsProps) {
  const { selectedBrand } = useContext(GlobalContext);
  const brandName =
    (selectedBrand as SelectedBrandWithName | null | undefined)?.brand_name ||
    "brand";
  const tooltip = toolTipContent[type as RRDToolTipType];

  const content =
    customContent ||
    (typeof tooltip === "function"
      ? tooltip({
          brandName,
          minCouponDurationDays,
          minCouponExpirationDate,
          maxCouponExpirationDate,
        })
      : tooltip);

  return <InfoToolTip content={content} />;
}

export default RRDDirectMailToolTips;
